import {
  Aggregator,
  type DataHubMetrics,
  type ExtendedMetric,
  type ForecastSettings,
  Renderer,
  type ReportConfig,
  Sort,
  TimeInterval,
} from "@doitintl/cmp-models";
import isEqual from "lodash/isEqual";

import { type CustomTimeRange, type MetricWSnap } from "../../../types";
import { showValuesPersistKey } from "../Context";
import { getDefaultForecastSettings } from "../report/config/ForecastSettings/utils";
import { type TimeRangeOption } from "../utilities";

export type ReportConfigStoreType = Omit<ReportConfig, "calculatedMetric" | "customTimeRange"> & {
  name: string;
  description: string;
  calculatedMetric: MetricWSnap | null;
  calcMetrics: MetricWSnap[];
  extendedMetrics: ExtendedMetric[];
  forecastSettings?: ForecastSettings | null;
  datahubMetrics: DataHubMetrics[];
  customTimeRange: CustomTimeRange | null;
  excludeSelectMetadataIds: Set<string>;
  timeRangeOption: TimeRangeOption;
  isGenerated?: boolean;
};

export enum ReportConfigKind {
  UPDATE_TIME_INTERVAL = "update_time_interval",
  REMOVE_FEATURES_FORECAST = "remove_features_forecast",
  ON_COMPARATIVE_MODE = "on_comparative_mode",
  TOGGLE_LOG_SCALE = "toggle_log_scale",
  SET_SHOW_VALUES_IN_FILTER_RESULTS = "set_values_in_filter_results",
  GENERATED_CONFIG_UPDATE = "generated_config_update",
}

export type ReportConfigAction = {
  type?: ReportConfigKind;
  payload?: Partial<ReportConfigStoreType>;
};

export const reportConfigReducer = (
  state: ReportConfigStoreType,
  action: ReportConfigAction
): ReportConfigStoreType => {
  const { payload } = action;
  let newState: ReportConfigStoreType;

  switch (action.type) {
    case ReportConfigKind.GENERATED_CONFIG_UPDATE: {
      if (!payload) {
        newState = state;
        break;
      }
      newState = { ...state, ...payload, isGenerated: true };
      break;
    }
    case ReportConfigKind.UPDATE_TIME_INTERVAL: {
      if (payload?.timeInterval === TimeInterval.YEAR) {
        newState = { ...state, ...payload, forecastSettings: undefined, isGenerated: false };
        break;
      }

      if (state.forecastSettings && payload?.timeInterval) {
        const forecastSettings = getDefaultForecastSettings(
          state?.timeRangeOption,
          payload.timeInterval,
          state.forecastSettings.mode
        );
        newState = { ...state, ...payload, forecastSettings, isGenerated: false };
        break;
      }

      newState = { ...state, ...payload, isGenerated: false };
      break;
    }
    case ReportConfigKind.REMOVE_FEATURES_FORECAST: {
      newState = { ...state, forecastSettings: null, isGenerated: false };
      break;
    }
    case ReportConfigKind.ON_COMPARATIVE_MODE: {
      let partialState: Partial<ReportConfigStoreType> = { aggregator: Aggregator.TOTAL };
      if (state.renderer === Renderer.TREEMAP) {
        partialState = { renderer: Renderer.TABLE };
      }

      partialState.colOrder = Sort.A_TO_Z;
      newState = { ...state, ...partialState, isGenerated: false };
      break;
    }
    case ReportConfigKind.TOGGLE_LOG_SCALE:
      newState = { ...state, logScale: !state.logScale, isGenerated: false };
      break;
    case ReportConfigKind.SET_SHOW_VALUES_IN_FILTER_RESULTS: {
      const checked = payload?.showValuesInFilterSearchResults ?? false;
      localStorage.setItem(showValuesPersistKey, checked.toString());
      newState = { ...state, ...payload, isGenerated: false };
      break;
    }
    default: {
      if (payload?.timeRangeOption && state.forecastSettings) {
        const forecastSettings = getDefaultForecastSettings(
          payload?.timeRangeOption,
          state.timeInterval,
          state.forecastSettings.mode
        );
        newState = { ...state, ...payload, forecastSettings, isGenerated: false };
        break;
      }

      const newVal = { ...state, ...payload, isGenerated: false };
      if (isEqual(newVal, state)) {
        newState = state;
        break;
      }
      newState = newVal;
      break;
    }
  }

  return newState;
};
